import * as _ from 'lodash';

/**
 * fetchers that are custom to FOSSA ('custom', 'archive', 'user', etc.)
 */
export const fossaFetchersMap = Object.freeze({
  archive: 'Archive',
  custom: 'Custom (provided build)',
  url: 'Global URL',
  'url-private': 'Private URL',
  user: 'User Defined',
  path: 'Local',
  sbom: 'SBOM',
  csbinary: 'Binary Decomposition Component',
  binary: 'Uploaded Binary',
});

export const CodeSentryFetcherName = 'csbinary';
export const BinaryFetcherName = 'binary';

type FOSSAFetcher = keyof (typeof fossaFetchersMap);

type FOSSAFetcherLabel = typeof fossaFetchersMap[FOSSAFetcher];

/**
 * fetchers that have a backing 3rd party registry/ecosystem. NOT FOSSA specific fetchers like 'custom' or 'archive'.
 * NOTE: DO NOT ADD ANY FOSSA SPECIFIC FETCHERS HERE (custom, archive, etc.).
 * This is used to serve package data in the global package inventory, so we don't want unexpected results to show up.
 */
export const thirdPartyFetchersMap = Object.freeze({
  apk: 'APK',
  bower: 'Bower',
  cargo: 'Cargo',
  cart: 'Carthage',
  comp: 'Composer',
  conan: 'Conan',
  conda: 'Conda',
  cpan: 'CPAN',
  cran: 'CRAN',
  deb: 'Debian',
  gem: 'RubyGem',
  git: 'Git',
  go: 'Golang',
  hackage: 'Hackage',
  hex: 'Hex',
  mvn: 'Maven',
  npm: 'NPM',
  nuget: 'Nuget',
  pip: 'Pip',
  pod: 'CocoaPods',
  pub: 'Pub',
  rpm: 'RPM',
  'rpm-generic': 'RPM',
  swift: 'Swift',
});

/**
 * ThirdPartyFetcher is the type of all third party fetchers.
 */
export type ThirdPartyFetcher = keyof (typeof thirdPartyFetchersMap);

/**
 * ThirdPartyFetcherLabel is the human-readable label for a third party fetcher.
 */
export type ThirdPartyFetcherLabel = typeof thirdPartyFetchersMap[ThirdPartyFetcher];

/**
 * Fetcher is the type of all fetchers
 */
export type Fetcher = FOSSAFetcher | ThirdPartyFetcher;

/**
 * Any human-readable label for a fetcher.
 */
type FetcherLabel = FOSSAFetcherLabel | ThirdPartyFetcherLabel;

//  This way we don't need to Object.keys it every time.
export const validThirdPartyFetchers = Object.keys(thirdPartyFetchersMap) as ThirdPartyFetcher[];

// Return a distinct fetcher list to prevent the same fetcher value appearing twice in the UI.
// If we don't remove the duplicate, the UI will show two 'RPM' options.
const duplicateFetchers = new Set<ThirdPartyFetcher>(['rpm']);
export const distinctFetchers =
  validThirdPartyFetchers.filter(fetcher => !duplicateFetchers.has(fetcher));

/**
 * Determines if a given value is a valid thirdPartyFetcher. Acts as a type guard so callers
 * receive a narrowed `ThirdPartyFetcher` on the truthy branch — pluggable directly into
 * `Array.prototype.filter` and Zod's `.refine()`.
 *
 * @param fetcher - Any value to test.
 */
export const isValidFetcher = (fetcher: unknown): fetcher is ThirdPartyFetcher =>
  validThirdPartyFetchers.includes(fetcher as ThirdPartyFetcher);

/**
 * The two locator types SBOM ingestion falls back to when it cannot identify an ecosystem for a
 * component: one with no supported PURL becomes `user`, and an unresolved generic PURL carrying a
 * download or VCS URL becomes `url-private`.
 *
 * They are grouped because of what that fallback costs them. Neither locator names a registry, so
 * neither can be resolved to a package whose versions we know, and their package field is not a
 * package name — it is a row id for `user` and an org-scoped URL for `url-private`. Their SBOM CPE
 * is all we have to go on, which makes them the only dependencies whose vulnerabilities arrive
 * through CPE matching rather than through a registry.
 */
export const sbomCpeFallbackFetchers = new Set<string>(['user', 'url-private']);

/**
 * allFetchersMap is a map of ALL available fetchers in FOSSA to their display name
 */
export const allFetchersMap: Record<Fetcher, FetcherLabel> = Object.freeze({
  ...thirdPartyFetchersMap,
  ...fossaFetchersMap,
});

/**
 * fetcherToPlainText takes in a fetcher string and returns a plain text string describing the fetcher:
 * 'mvn' -> 'Maven', 'pod' => 'Cocoapods'
 */
export function fetcherToPlainText(fetcher: Fetcher): FetcherLabel | undefined {
  return allFetchersMap[fetcher];
}

/** Whether a raw string is one of FOSSA's known fetcher codes (FOSSA-specific or third-party). */
export function isFetcher(value: string): value is Fetcher {
  return Object.hasOwn(allFetchersMap, value);
}

/**
 * languageToFetcherMap maps a programming language to a list of potential fetchers that could include that language
 */
export const languageToFetcherMap = Object.freeze({
  '.net': ['nuget'],
  'c#': ['nuget'],
  java: ['mvn'],
  javascript: ['npm', 'bower'],
  go: ['git'],
  php: ['comp'],
  python: ['pip'],
  r: ['cran'],
  ruby: ['gem'],
  rust: ['cargo'],
});

/**
 * linuxFetchers is a readonly list of all linux fetchers. These fetchers fetch data using the Knowledge API, which queries Ramjet (as of 5/20/22)
 */
type LinuxFetcher = 'apk' | 'deb' | 'rpm-generic';

export const linuxFetchers: readonly LinuxFetcher[] = Object.freeze([
  'apk',
  'deb',
  'rpm-generic',
]);

/**
 * isLinuxFetcher returns true if @fetcher is a "linux" fetcher. This includes fetchers fetch data using the Knowledge API, which queries Ramjet (as of 5/20/22)
 */
export function isLinuxFetcher(fetcher?: string | null): fetcher is LinuxFetcher {
  if (!fetcher) {
    return false;
  }

  const lowerCaseFetcher = fetcher.toLowerCase();
  return linuxFetchers.some(f => f === lowerCaseFetcher);
}

/**
 * Who emits the PackageVersions event for a fetcher's packages, if anyone.
 * - 'core-resolve': Core emits at the end of fromLocator's revision sync, when
 *   getMetadata returns the package's full published list from a public registry.
 * - 'crawler': a crawler owns the list (ramjet for Linux, Sparkle for conda). Core's
 *   resolve path must not emit: two producers for one key would race with different orders.
 * - 'non-orderable': git-shaped history with no version list (git, and swift and cart,
 *   which resolve to git). Nothing emits.
 * - 'no-version-list': getMetadata returns no list, one entry, or a customer-owned
 *   artifact. Nothing emits.
 */
export type PackageVersionsSource = 'core-resolve' | 'crawler' | 'non-orderable' | 'no-version-list';

/**
 * Keyed by the value getFetcher switches on, which is `locator.fetcher.value` once a fetcher has
 * resolved the locator: every Fetcher, plus the three extras getFetcher also dispatches. The
 * `satisfies` makes a missing or misspelled Fetcher a compile error; a unit test catches a
 * getFetcher case that isn't a Fetcher at all.
 */
const packageVersionsSources = {
  npm: 'core-resolve',
  mvn: 'core-resolve',
  bower: 'core-resolve',
  cran: 'core-resolve',
  pod: 'core-resolve',
  cargo: 'core-resolve',
  comp: 'core-resolve',
  cpan: 'core-resolve',
  gem: 'core-resolve',
  go: 'core-resolve',
  hackage: 'core-resolve',
  hex: 'core-resolve',
  nuget: 'core-resolve',
  pip: 'core-resolve',
  pub: 'core-resolve',
  conda: 'crawler',
  apk: 'crawler',
  deb: 'crawler',
  'rpm-generic': 'crawler',
  // git and ecosystems that resolve to git (swift and carthage) are non-orderable.
  git: 'non-orderable',
  swift: 'non-orderable',
  cart: 'non-orderable',
  // conan has a version list, but index-service doesn't accept conan locators yet, so it would
  // refuse every conan event. Nothing emits until a follow-up PR teaches index-service to order
  // conan versions and moves conan back to core-resolve.
  conan: 'no-version-list',
  custom: 'no-version-list',
  rpm: 'no-version-list',
  sbom: 'no-version-list',
  binary: 'no-version-list',
  url: 'no-version-list',
  'url-private': 'no-version-list',
  user: 'no-version-list',
  archive: 'no-version-list',
  path: 'no-version-list',
  csbinary: 'no-version-list',
  snippet: 'no-version-list',
  sourceforge: 'no-version-list',
  stackoverflow: 'no-version-list',
} satisfies Record<Fetcher | 'snippet' | 'sourceforge' | 'stackoverflow', PackageVersionsSource>;

// Widened to `string` keys so callers can index it with a raw `locator.fetcher.value`.
export const packageVersionsSourceByFetcher: Readonly<Record<string, PackageVersionsSource>> =
  Object.freeze(packageVersionsSources);

export const linuxDistrosByFetcher = Object.freeze({
  apk: [{ value: 'alpine', label: 'Alpine' }],
  deb: [{ value: 'ubuntu', label: 'Ubuntu' }, { value: 'debian', label: 'Debian' }],
  'rpm-generic': [
    { value: 'redhat', label: 'Redhat' },
    { value: 'centos', label: 'CentOS' },
    { value: 'oraclelinux', label: 'Oracle Linux' },
    { value: 'rhel', label: 'Redhat Enterprise Linux' },
    { value: 'fedora', label: 'Fedora' },
    { value: 'sles', label: 'SUSE Linux Enterprise Server' },
  ],
});

export const commonLinuxArchs = Object.freeze([
  'all',
  'noarch',
  'amd64',
  'arm64',
  'x86_64',
  'aarch64',
]);

/**
 * externalApiSanitizedFetchers is a list of fetchers that make external API calls during sanitization for removeRegistryFromLocator.
 */
export const externalApiSanitizedFetchers: Set<Fetcher> = new Set<Fetcher>(['npm']);

/**
 * Fetchers whose package names are case-insensitive in their ecosystem.
 * VulnCalculation rows for the same logical revision can be stored under
 * either the original or lowercased package name, so lookups (in VulnScanner
 * and SafeVersionManager) need to consider both casings.
 */
export const caseInsensitiveFetchers: ReadonlySet<string> = new Set(['pip', 'nuget']);

// Sort the language dropdown options, and turn them into value+label pairs.
// Remove any fetchers that are not distinct.
const sortedDropdownOptions = _.sortBy(
  Object.entries(thirdPartyFetchersMap)
    .map(([value, label]) => ({ value: value as ThirdPartyFetcher, label })),
  elem => elem.label,
);
export const FETCHER_DROPDOWN_OPTIONS =
  sortedDropdownOptions.filter(({ value }) => distinctFetchers.includes(value));
